'use client';

import { MEMBER_TYPE_LABELS } from '@slims/shared/types';

import {
	deriveGseProfileCode,
	directionOfMeterTipo,
	GSE_PROFILE_BY_CODE,
	isDomestico,
	selectableGseProfiles,
} from '@slims/shared/energy';
import { TypicalProfileCard } from '@slims/ui/molecules';
import { useState } from 'react';
import { toast } from 'sonner';
import type { Meter } from '@/hooks/use-meters';

/**
 * Il profilo tipico del punto di misura, sotto ai suoi dati di connessione.
 *
 * Sta qui e non in una pagina a parte perché è del punto di misura: la curva
 * che il POD mostra finché non arriva una misura è questa, e i dettagli che la
 * determinano — ruolo, tipo di socio, accumulo, impianti — vanno letti accanto
 * al punto che profilano.
 *
 * Il codice si **deduce** da quei dettagli e si riscrive da sé quando cambiano.
 * La tendina non toglie la deduzione: la scavalca solo dove serve, cioè dove la
 * deduzione non può arrivare (`PIRM`, la ricarica veicoli, non è desumibile da
 * nessun campo dell'anagrafica) o dove sbaglia perché `member.tipo` è
 * un'approssimazione della tipologia del Registro Centrale. Finché nessuno
 * sceglie, `gseProfileCode` resta null e comanda la deduzione.
 */
export interface TypicalProfileSectionProps {
	meter: Meter;
	/** Ruolo del socio, la prima lettera del codice GSE. */
	ruolo?: string | null;
	/** Tipo di socio: distingue il domestico dagli «altri usi». */
	tipo?: string | null;
	hasStorage?: boolean | null;
	/** Scrive `gseProfileCode` sul punto di misura. */
	onUpdate: (id: string, patch: Record<string, unknown>) => Promise<unknown>;
}

const RUOLO_LABEL: Record<string, string> = {
	consumer: 'Consumatore',
	producer: 'Produttore',
	prosumer: 'Prosumer',
};

const TIPO_LABEL: Record<string, string> = MEMBER_TYPE_LABELS;

export function TypicalProfileSection({
	meter,
	ruolo,
	tipo,
	hasStorage,
	onUpdate,
}: TypicalProfileSectionProps) {
	const [saving, setSaving] = useState(false);
	const profile = meter.profiloTipico;
	if (!profile) return null;

	const def = GSE_PROFILE_BY_CODE[profile.codice];
	const direction = directionOfMeterTipo(meter.tipo);
	// Il codice che uscirebbe senza scelta manuale: è quello che il badge
	// «il dedotto sarebbe X» mostra, e il bersaglio di «Torna al dedotto».
	const derivedCode = deriveGseProfileCode({
		ruolo: (ruolo as 'consumer' | 'producer' | 'prosumer') ?? 'consumer',
		tipo,
		hasStorage,
		tipologiaUtenza: meter.tipologiaUtenza,
		direction,
	});

	async function handleSelect(code: string | null) {
		setSaving(true);
		try {
			await onUpdate(meter.id, { gseProfileCode: code });
			toast.success(
				code
					? `Profilo GSE impostato su ${code}. La curva tipica si sta rigenerando.`
					: 'Profilo GSE tornato al dedotto. La curva tipica si sta rigenerando.',
			);
		} catch {
			toast.error('Errore nel salvataggio del profilo GSE');
		} finally {
			setSaving(false);
		}
	}

	return (
		<TypicalProfileCard
			embedded
			codice={profile.codice}
			label={def?.label ?? profile.codice}
			shape={def?.shape ?? ''}
			annualKwh={profile.annualKwh}
			basis={profile.basis}
			options={selectableGseProfiles(direction).map((p) => ({
				code: p.code,
				label: p.label,
			}))}
			selected={meter.gseProfileCode ?? null}
			derivedCode={derivedCode}
			onSelect={handleSelect}
			disabled={saving}
			derivedFrom={[
				{ label: 'Ruolo', value: RUOLO_LABEL[ruolo ?? ''] ?? '-' },
				{
					label: 'Uso',
					value: isDomestico(tipo)
						? 'Domestico'
						: `Altri usi${tipo ? ` (${TIPO_LABEL[tipo] ?? tipo})` : ''}`,
				},
				{ label: 'Accumulo', value: hasStorage ? 'Sì' : 'No' },
			]}
		/>
	);
}
